import { useState, useEffect, useRef, useCallback } from 'react';

import { useAuthInfo } from '@propelauth/react';
import { useQueryClient } from 'react-query';
import { io, Socket } from 'socket.io-client';

import { EnvVars } from '../../../../../../../../../../environment';
import { createAudioStream, resetAudioState } from '../components/audio-stream';
import { useTranscriptInconsistenciesStore } from '../stores/transcriptInconsistenciesStore';
import { TranscriptionEvent } from '../types/transcription-events';
import { LiveTranscriptBlock } from '../types/transcriptTypes';
import { TranscriptStatus } from '@/../../backend/src/matter/subset/transcription/transcript-enums';
import { QueryEntity } from '@/api/types';
import {
  TranscriptInconsistency,
  TranscriptBlock,
  TranscriptSpeaker,
  TranscriptInternalSpeaker,
} from '@/api/types/backend-dto-types';

interface TranscriptionResult {
  transcript: string;
  isPartial: boolean;
  startTime?: number;
  endTime?: number;
  blockId?: string;
  internalSpeaker?: {
    id: string;
    internalName: string;
    speakerId: string | null;
    speaker?: {
      id: string;
      name: string;
      color?: string;
    } | null;
  };
}

interface DiarizationCorrectionEvent {
  blockId: string;
  correctionId: string;
  confidence: number;
  originalContent: string;
  correctedSegments: Array<{
    content: string;
    speakerId: string;
    startTime: number;
    endTime: number;
  }>;
  analysis: {
    primaryIssue: string;
    improvementSource: 'fast' | 'real-time' | 'hybrid';
    speakerChanges: number;
    reasoning: string;
  };
}

export interface LogicalSpeaker {
  id: string;
  name: string;
  color: string;
}

export interface InternalSpeaker {
  id: string;
  internalName: string;
  sessionNumber: number;
  displayName: string;
  logicalSpeakerId?: string;
  logicalSpeakerName?: string;
  logicalSpeakerColor?: string;
}

export const useTranscription = (
  caseId: string | null,
  transcriptId?: string | null,
  transcript?: {
    phraseList?: string[] | null;
    status?: string;
    blocks?: TranscriptBlock[];
    speakers?: TranscriptSpeaker[];
    internalSpeakers?: TranscriptInternalSpeaker[];
  } | null,
) => {
  const queryClient = useQueryClient();
  const authInfo = useAuthInfo();
  const accessToken = authInfo.accessToken;
  const { addInconsistency } = useTranscriptInconsistenciesStore();

  // Check if this is a completed transcript (playback mode)
  const isPlaybackMode = transcript?.status === TranscriptStatus.Completed;

  const [recordingState, setRecordingState] = useState<{
    isRecording: boolean;
    isPaused: boolean;
    isConnected: boolean;
    isCompleting: boolean;
  }>({
    isRecording: false,
    isPaused: transcript?.status === TranscriptStatus.Paused,
    isConnected: false,
    isCompleting: false,
  });

  const [blocks, setBlocks] = useState<LiveTranscriptBlock[]>([]);
  const [logicalSpeakers, setLogicalSpeakers] = useState<LogicalSpeaker[]>([]);
  const [internalSpeakers, setInternalSpeakers] = useState<InternalSpeaker[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hasConnectedBefore, setHasConnectedBefore] = useState(false);

  const socketRef = useRef<Socket | null>(null);
  const audioStreamRef = useRef<{
    destroy: () => void;
    workletMessageIterator: AsyncIterableIterator<MessageEvent<any>>;
  } | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const recordingStateRef = useRef(recordingState);

  // Extract individual state values for easier access
  const { isRecording, isPaused, isConnected, isCompleting } = recordingState;

  // Keep ref in sync with state
  useEffect(() => {
    recordingStateRef.current = recordingState;
  }, [recordingState]);

  // Initialize state from saved transcript when it loads
  useEffect(() => {
    const isPausedState = transcript?.status === 'paused';

    setRecordingState((prev) => ({
      ...prev,
      isPaused: isPausedState,
    }));

    // Initialize blocks from saved transcript
    if (transcript?.blocks && Array.isArray(transcript.blocks)) {
      const liveBlocks: LiveTranscriptBlock[] = transcript.blocks.map((block) => ({
        ...block,
        isPartial: false,
        audioReady: true,
      }));
      setBlocks(liveBlocks);
    }

    // Initialize speakers from saved transcript
    if (transcript?.speakers && Array.isArray(transcript.speakers)) {
      setLogicalSpeakers(
        transcript.speakers.map((speaker) => ({
          id: speaker.id,
          name: speaker.name,
          color: speaker.color,
        })),
      );
    }

    // Initialize internal speakers from saved transcript
    if (transcript?.internalSpeakers && Array.isArray(transcript.internalSpeakers)) {
      setInternalSpeakers(
        transcript.internalSpeakers.map((internalSpeaker) => ({
          id: internalSpeaker.id,
          internalName: internalSpeaker.internalName,
          sessionNumber: 1,
          displayName: internalSpeaker.speaker?.name || `${internalSpeaker.internalName}`,
          logicalSpeakerId: internalSpeaker.speaker?.id,
          logicalSpeakerName: internalSpeaker.speaker?.name,
        })),
      );
    }
  }, [transcript?.status, transcript?.blocks, transcript?.speakers, transcript?.internalSpeakers]);

  useEffect(() => {
    if (!caseId || !accessToken) return;

    // Only create new socket if we don't have one or if caseId changed
    if (socketRef.current && socketRef.current.connected) {
      return;
    }

    // Disconnect existing socket if we're creating a new one
    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    const socket = io(`${EnvVars.API_URL}/transcription`, {
      auth: {
        token: accessToken,
      },
      transports: ['websocket', 'polling'],
      autoConnect: true,
      withCredentials: true,
    });

    socket.on('connect', () => {
      setRecordingState((prev) => ({ ...prev, isConnected: true }));
      setError(null);

      // Re-establish transcription session if we were recording before disconnect and we've connected before
      if (hasConnectedBefore) {
        if (recordingStateRef.current.isRecording && transcriptId) {
          socket.emit(TranscriptionEvent.StartTranscription, {
            caseId,
            transcriptId,
            transcriptName: 'Live Transcription',
            options: {
              languageCode: 'en-US',
              sampleRate: 48000,
              enableSpeakerLabels: true,
              enablePartialResults: true,
              phrases: transcript?.phraseList ?? [],
            },
          });
        }
      }
      setHasConnectedBefore(true);
    });

    socket.on('disconnect', () => {
      setRecordingState((prev) => ({ ...prev, isConnected: false }));
    });

    socket.on(TranscriptionEvent.TranscriptionResult, (result: TranscriptionResult) => {
      // Create block with speaker information from backend
      const block: LiveTranscriptBlock = {
        id: result.blockId ?? `${Date.now()}-${Math.random()}`,
        content: result.transcript,
        timestamp: result.startTime ?? 0,
        transcriptId: transcriptId ?? '',
        sessionId: '',
        duration: null,
        isWavExtracted: false,
        removed: false,
        createdDate: new Date(),
        isPartial: result.isPartial,
        audioReady: false,
        internalSpeaker: result.internalSpeaker ?? null,
        internalSpeakerId: result.internalSpeaker?.id ?? null,
        correction: null,
      };

      setBlocks((prevBlocks) => {
        let newBlocks;
        const finalBlocks = prevBlocks.filter((block) => !block.isPartial);

        if (result.isPartial) {
          newBlocks = [...finalBlocks, block];
        } else {
          newBlocks = [...finalBlocks, { ...block, isPartial: false }];
        }
        return newBlocks;
      });

      // Track internal speakers that are unmapped (using backend data structure)
      if (!result.isPartial && result.internalSpeaker && result.internalSpeaker.speakerId === null) {
        setInternalSpeakers((prev) => {
          const existingInternal = prev.find((s) => s.id === result.internalSpeaker!.id);
          if (!existingInternal) {
            // Use the display name provided by the backend (includes session number if needed)
            const displayName = result.internalSpeaker?.internalName || 'Unknown Speaker';

            // Extract session number from backend display name if present
            const sessionMatch = displayName.match(/Session (\d+)/);
            const sessionNumber = sessionMatch ? parseInt(sessionMatch[1]) : 1;

            return [
              ...prev,
              {
                id: result.internalSpeaker!.id,
                internalName: result.internalSpeaker!.internalName,
                sessionNumber,
                displayName,
              },
            ];
          }
          return prev;
        });
      }

      // Add or update logical speakers when they're mapped
      if (!result.isPartial && result.internalSpeaker?.speaker) {
        setLogicalSpeakers((prev) => {
          const existingSpeaker = prev.find((s) => s.id === result.internalSpeaker!.speaker!.id);
          if (!existingSpeaker) {
            return [
              ...prev,
              {
                id: result.internalSpeaker!.speaker!.id,
                name: result.internalSpeaker!.speaker!.name,
                color: result.internalSpeaker!.speaker!.color || 'blue',
              },
            ];
          }
          return prev;
        });
      }
    });

    socket.on(TranscriptionEvent.TranscriptionError, (data: { error: string }) => {
      setError(data.error);
      stopRecording();
    });

    socket.on(TranscriptionEvent.TranscriptionPaused, () => {
      setRecordingState((prev) => ({ ...prev, isPaused: true }));
    });

    socket.on(TranscriptionEvent.TranscriptionCompleted, () => {
      setRecordingState((prev) => ({ ...prev, isRecording: false, isPaused: false, isCompleting: false }));

      if (transcriptId) {
        queryClient.invalidateQueries([QueryEntity.Transcript, { transcriptId }]);
      }
    });

    socket.on(
      TranscriptionEvent.InconsistencyDetected,
      (data: { inconsistency: TranscriptInconsistency; transcriptId: string; caseId: string }) => {
        if (data.transcriptId) {
          addInconsistency(data.transcriptId, data.inconsistency);

          // When new inconsistencies are detected, convert any reprocessing inconsistencies to invalidated
          // This indicates the inconsistency detection pipeline has completed for edited blocks
          const { convertReprocessingToInvalidated } = useTranscriptInconsistenciesStore.getState();
          convertReprocessingToInvalidated(data.transcriptId);
        }
      },
    );

    socket.on(TranscriptionEvent.BlockWavReady, (data: { blockId: string; transcriptId: string }) => {
      setBlocks((prevBlocks) =>
        prevBlocks.map((block) => (block.id === data.blockId ? { ...block, audioReady: true } : block)),
      );
    });

    socket.on(
      TranscriptionEvent.SpeakerMapped,
      (data: {
        internalSpeakerId: string;
        logicalSpeakerId: string;
        logicalSpeakerName: string;
        logicalSpeakerColor: string;
      }) => {
        setInternalSpeakers((prev) => prev.filter((speaker) => speaker.id !== data.internalSpeakerId));

        setLogicalSpeakers((prevSpeakers) => {
          const existingSpeaker = prevSpeakers.find((s) => s.id === data.logicalSpeakerId);

          setBlocks((prevBlocks) =>
            prevBlocks.map((block) =>
              block.internalSpeaker?.id === data.internalSpeakerId
                ? {
                    ...block,
                    internalSpeaker: {
                      ...block.internalSpeaker,
                      speakerId: data.logicalSpeakerId,
                      speaker: {
                        id: data.logicalSpeakerId,
                        name: data.logicalSpeakerName,
                        color: data.logicalSpeakerColor,
                      } as any,
                    },
                  }
                : block,
            ),
          );

          return existingSpeaker
            ? prevSpeakers
            : [
                ...prevSpeakers,
                {
                  id: data.logicalSpeakerId,
                  name: data.logicalSpeakerName,
                  color: data.logicalSpeakerColor,
                },
              ];
        });
      },
    );

    socket.on(TranscriptionEvent.DiarizationCorrectionAvailable, (data: DiarizationCorrectionEvent) => {
      setBlocks((prevBlocks) =>
        prevBlocks.map((block) =>
          block.id === data.blockId
            ? {
                ...block,
                correction: {
                  id: data.correctionId,
                  confidence: data.confidence,
                  originalContent: data.originalContent,
                  status: 'pending',
                  correctedSegments: data.correctedSegments,
                  analysis: data.analysis,
                },
              }
            : block,
        ),
      );
    });

    socket.on(TranscriptionEvent.BlockRemoved, (data: { blockId: string }) => {
      setBlocks((prevBlocks) => prevBlocks.filter((block) => block.id !== data.blockId));
    });

    socket.on(
      TranscriptionEvent.BlockUpdated,
      (data: {
        blockId: string;
        content: string;
        speakerId: string | null;
        speakerName: string | null;
        timestamp: number;
        duration: number | null;
      }) => {
        // Mark inconsistencies that reference this block as invalidated
        if (transcriptId) {
          const { markBlockInconsistenciesAsInvalidated } = useTranscriptInconsistenciesStore.getState();
          markBlockInconsistenciesAsInvalidated(transcriptId, data.blockId);
        }

        setBlocks((prevBlocks) => {
          const newColor = prevBlocks.find((b) => b.internalSpeaker?.speaker?.id === data.speakerId)?.internalSpeaker
            ?.speaker?.color;

          return prevBlocks.map((block) => {
            if (block.id === data.blockId) {
              const updatedBlock: LiveTranscriptBlock = {
                ...block,
                content: data.content,
                internalSpeaker:
                  data.speakerId && data.speakerName
                    ? block.internalSpeaker
                      ? {
                          ...block.internalSpeaker,
                          speaker: {
                            id: data.speakerId,
                            name: data.speakerName,
                            color: newColor,
                          },
                        }
                      : null
                    : block.internalSpeaker,
              };
              return updatedBlock;
            }
            return block;
          });
        });
      },
    );

    socket.on(
      TranscriptionEvent.CorrectionProcessingComplete,
      (data: { correctionId: string; blockId: string; success: boolean; error?: string; newBlockIds?: string[] }) => {
        if (data.success) {
          // For accepted corrections, remove the correction from the original block (it will be deleted)
          // and refresh the transcript to show new blocks
          setBlocks((prevBlocks) =>
            prevBlocks.map((block) => (block.id === data.blockId ? { ...block, correction: null } : block)),
          );

          // Refresh the transcript to show new blocks created from correction
          if (data.newBlockIds && data.newBlockIds.length > 0 && transcriptId) {
            queryClient.invalidateQueries([QueryEntity.Transcript, { transcriptId }]);
          }
        } else if (data.error) {
          setError(`Correction processing failed: ${data.error}`);
        }
      },
    );

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      setHasConnectedBefore(false); // Reset connection tracking
    };
  }, [caseId, transcriptId, transcript?.phraseList]);

  const startRecording = useCallback(async () => {
    // Prevent recording for completed transcripts
    if (isPlaybackMode) {
      setError('Cannot record audio for completed transcripts');
      return;
    }

    try {
      setError(null);

      if (socketRef.current && !socketRef.current.connected) {
        socketRef.current.connect();
      }

      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Connection timeout')), 5000);
        socketRef.current?.on('connect', () => {
          clearTimeout(timeout);
          resolve();
        });
        if (socketRef.current?.connected) {
          clearTimeout(timeout);
          resolve();
        }
      });

      const audioStream = await createAudioStream(3600); // 1hr max for now
      audioStreamRef.current = audioStream;

      const phrases = transcript?.phraseList ?? [];

      socketRef.current?.emit(TranscriptionEvent.StartTranscription, {
        caseId,
        transcriptId,
        transcriptName: 'Live Transcription',
        options: {
          languageCode: 'en-US',
          sampleRate: audioStream.sampleRate,
          enableSpeakerLabels: true,
          enablePartialResults: true,
          phrases: phrases,
        },
      });

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // Set recording state BEFORE starting audio processing
      setRecordingState((prev) => ({ ...prev, isRecording: true, isPaused: false }));

      // Process audio data from worklet
      (async () => {
        try {
          for await (const messageEvent of audioStream.workletMessageIterator) {
            if (abortController.signal.aborted) {
              break;
            }

            if (!recordingStateRef.current.isRecording || recordingStateRef.current.isPaused) {
              continue;
            }

            const { pcmData } = messageEvent.data;

            socketRef.current?.emit(TranscriptionEvent.AudioData, {
              audioData: Array.from(pcmData),
              sampleRate: audioStream.sampleRate,
            });
          }
        } catch {
          if (!abortController.signal.aborted) {
            setError('Error processing audio stream');
          }
        } finally {
          if (audioStreamRef.current && !abortController.signal.aborted) {
            audioStreamRef.current.destroy();
            audioStreamRef.current = null;
          }
        }
      })();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start recording';
      setError(errorMessage);
    }
  }, [caseId, recordingState.isConnected, transcript, isPlaybackMode]);

  const stopRecording = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.destroy();
      audioStreamRef.current = null;
    }

    resetAudioState();

    socketRef.current?.emit(TranscriptionEvent.StopTranscription);

    setRecordingState((prev) => ({ ...prev, isRecording: false, isPaused: false }));
  }, []);

  const pauseRecording = useCallback(() => {
    // Prevent pausing for completed transcripts
    if (isPlaybackMode) {
      setError('Cannot pause completed transcripts');
      return;
    }

    // Stop the audio stream to prevent buffering during pause
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.destroy();
      audioStreamRef.current = null;
    }

    socketRef.current?.emit(TranscriptionEvent.PauseTranscription);
    setRecordingState((prev) => ({ ...prev, isPaused: true }));
  }, [isPlaybackMode]);

  const completeRecording = useCallback(() => {
    // Prevent completing for completed transcripts
    if (isPlaybackMode) {
      setError('Cannot complete already completed transcripts');
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.destroy();
      audioStreamRef.current = null;
    }

    // Set completing state before emitting the event
    setRecordingState((prev) => ({ ...prev, isRecording: false, isPaused: false, isCompleting: true }));

    socketRef.current?.emit(TranscriptionEvent.CompleteTranscription, { transcriptId });
  }, [isPlaybackMode]);

  const mapInternalSpeaker = useCallback((internalSpeakerId: string, logicalSpeakerName: string) => {
    socketRef.current?.emit(TranscriptionEvent.MapInternalSpeaker, {
      transcriptId,
      internalSpeakerId,
      logicalSpeakerName,
    });
  }, []);

  const acceptDiarizationCorrection = useCallback((correctionId: string, blockId: string) => {
    // Immediately mark as accepted in UI for better UX
    setBlocks((prevBlocks) =>
      prevBlocks.map((block) =>
        block.id === blockId && block.correction?.id === correctionId
          ? { ...block, correction: { ...block.correction, status: 'accepted' } }
          : block,
      ),
    );

    socketRef.current?.emit(TranscriptionEvent.AcceptDiarizationCorrection, {
      correctionId,
      blockId,
    });
  }, []);

  const rejectDiarizationCorrection = useCallback((correctionId: string, blockId: string) => {
    // Immediately mark as rejected in UI - this will hide it from display
    setBlocks((prevBlocks) =>
      prevBlocks.map((block) =>
        block.id === blockId && block.correction?.id === correctionId
          ? { ...block, correction: { ...block.correction, status: 'rejected' } }
          : block,
      ),
    );

    socketRef.current?.emit(TranscriptionEvent.RejectDiarizationCorrection, {
      correctionId,
      blockId,
    });
  }, []);

  const updateBlock = (blockId: string, content?: string, speakerId?: string | null) => {
    if (!socketRef.current?.connected) {
      setError('Not connected to transcription service');
      return;
    }

    if (!caseId) {
      setError('Case ID is required');
      return;
    }

    // Also update the block in the local state
    // NOTE: I don't think this is doing anything at all
    setBlocks((prevBlocks) => {
      const newColor = prevBlocks.find((b) => b.internalSpeaker?.speaker?.id === speakerId)?.internalSpeaker?.speaker
        ?.color;

      return prevBlocks.map((block) =>
        block.id === blockId
          ? {
              ...block,
              ...(content !== undefined && { content }),
              ...(speakerId !== undefined && { speakerId }),
              ...(newColor !== undefined && {
                internalSpeaker: block.internalSpeaker
                  ? {
                      ...block.internalSpeaker,
                      speaker: block.internalSpeaker.speaker
                        ? { ...block.internalSpeaker.speaker, color: newColor }
                        : null,
                    }
                  : null,
              }),
            }
          : block,
      );
    });

    socketRef.current.emit(TranscriptionEvent.UpdateBlock, {
      blockId,
      caseId,
      content,
      speakerId,
    });
  };

  // Filter out blocks with rejected corrections from display
  const displayBlocks = blocks.map((block) => ({
    ...block,
    correction: block.correction?.status === 'rejected' ? null : block.correction,
  }));

  return {
    isRecording,
    isPaused,
    isConnected,
    isCompleting,
    blocks: displayBlocks,
    logicalSpeakers,
    internalSpeakers,
    error,
    startRecording,
    stopRecording,
    pauseRecording,
    completeRecording,
    mapInternalSpeaker,
    acceptDiarizationCorrection,
    rejectDiarizationCorrection,
    updateBlock,
  };
};
